import type { CSSProperties, MouseEventHandler } from 'react';
import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import { ElfsightGlyph, Eye } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { Popout, zIndexLayer } from '@elfsight/widget-modal';
import { openWindowByLink, useRerender } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import type { Rect } from 'react-use-rect';
import { useRect } from 'react-use-rect';
import {
  DEFAULT_TEXT,
  DEFAULT_TEXT_MINI,
  HEIGHT_LIMIT,
  WIDTH_LIMIT
} from './constants';
import { createDeactivationURL } from './links';
import type { Deactivation2Config, PositionValue } from './types';
import { usePosition } from './utils';

export function Deactivation2({
  appAlias,
  displayDeactivation = false,
  deactivationURL = '',
  text,
  style,
  containerStyle,
  withDeactivationMessage = true,
  children,
  childrenRef
}: Deactivation2Config) {
  const url = createDeactivationURL(deactivationURL, appAlias);

  const ref = useRef<HTMLDivElement>();
  const [containerRect, setContainerRect] = useState<Rect | null>();
  const [containerRef] = useRect(setContainerRect, { resize: true });

  const validRefs = [ref, containerRef, childrenRef].filter((ref) => !!ref);
  const mergedRefs = useMergedRef(...validRefs);

  const { width = 0, height = 0 } = containerRect || {};
  const layout =
    width < WIDTH_LIMIT || height < HEIGHT_LIMIT ? 'short' : 'full';

  const [verticalPosition, horizontalPosition] = usePosition(containerRect);

  const deactivationClickHandler: MouseEventHandler = (e) => {
    openWindowByLink(e, {
      type: 'url',
      value: url.toString(),
      rawValue: url.toString(),
      target: '_blank'
    });
  };

  const rerender = useRerender();
  useEffect(() => {
    window.addEventListener('resize', rerender);
    return () => window.removeEventListener('resize', rerender);
  }, []);

  if (!isValidElement(children)) {
    throw new Error('Invalid Deactivation Element');
  }

  if (!displayDeactivation) {
    return cloneElement(children, { ...children?.props, ref: mergedRefs });
  }

  return (
    <MainContainer style={containerStyle}>
      {cloneElement(children, {
        ...children?.props,
        ref: mergedRefs,
        style: { ...children?.props?.style, opacity: 0.3 }
      })}
      {withDeactivationMessage && (
        <>
          {layout === 'short' ? (
            <Popout
              offsetTop={-4}
              direction={verticalPosition === 'top' ? 'upward' : 'downward'}
              alignment={horizontalPosition}
              show
              anchorElement={ref.current as Element}
              zIndex={zIndexLayer.deactivation}
            >
              <ShortLayout
                onClick={deactivationClickHandler}
                text={text}
                style={style}
                url={url.toString()}
                coverRect={containerRect}
              />
            </Popout>
          ) : (
            <FullLayout
              onClick={deactivationClickHandler}
              text={text}
              style={style}
              url={url.toString()}
              coverRect={containerRect}
            />
          )}
        </>
      )}
    </MainContainer>
  );
}

const FullLayout = ({ text, style, url, onClick }: ComponentType) => {
  const titleText = text ?? DEFAULT_TEXT;

  return (
    <Container onClick={onClick} style={style}>
      <Icon
        component={ElfsightGlyph}
        size={22}
        fill="#F93262"
        style={{ padding: '2px' }}
      />
      <Inner>
        <Title>{titleText}</Title>
        <ReactivateNowButton
          href={url}
          target="_blank"
          rel="noreferrer"
          style={style}
        >
          Reactivate Now
        </ReactivateNowButton>
      </Inner>
      <CaptionContainer>
        <Eye width="12" height="12" fill="rgba(17, 17, 17, 0.5)" />
        <Caption>Only you can see this</Caption>
      </CaptionContainer>
    </Container>
  );
};

const ShortLayout = ({ text, style, url, onClick }: ComponentType) => {
  const titleText = `${text ?? DEFAULT_TEXT_MINI} `;

  return (
    <ShortContainer onClick={onClick} style={style}>
      <Icon
        component={ElfsightGlyph}
        size={16}
        fill="#F93262"
        style={{ padding: '1px' }}
      />
      <div>
        <Title _short>
          {titleText}
          <ReactivateNowTitle href={url} target="_blank" rel="noreferrer">
            Reactivate Now
          </ReactivateNowTitle>
        </Title>
        <CaptionContainer _short>
          <Eye width="10" height="10" fill="rgba(17, 17, 17, 0.5)" />
          <Caption _short>Only you can see this</Caption>
        </CaptionContainer>
      </div>
    </ShortContainer>
  );
};

type ComponentType = {
  coverRect: Rect | null | undefined;
  url: string;
  position?: PositionValue;
  text?: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler;
};

type BlockType = {
  _short?: boolean;
};

const MainContainer = styled.div`
  & * {
    pointer-events: none;
  }
  pointer-events: none;
  position: relative;
  align-content: center;
  align-items: center;
  animation: none;
  border: none;
  border-radius: inherit;
  bottom: 0;
  box-sizing: border-box;
  display: flex;
  float: none;
  height: auto;
  left: 0;
  margin: 0;
  opacity: 1;
  padding: 0;
  right: 0;
  text-align: left;
  text-indent: 0;
  top: 0;
  transform: none;
  justify-content: center;
  visibility: visible;
  zoom: 1;
  z-index: 999999998;
`;

const Container = styled.div`
  cursor: pointer;
  pointer-events: all !important;
  & * {
    pointer-events: all !important;
  }
  display: flex;
  width: 300px;
  height: 146px;
  padding: 12px 8px;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  position: absolute;
  border-radius: 4px;
  border: 1px solid #ff3838;
  background: rgb(255, 236, 236);
  z-index: 1;
  box-sizing: border-box;
  font-family: Arial, sans-serif;
`;

const Inner = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Title = styled.div<BlockType>`
  color: #111;
  text-align: ${({ _short }) => (_short ? 'left' : 'center')};
  font-size: ${({ _short }) => (_short ? 11 : 14)}px;
  font-style: normal;
  font-weight: 400;
  line-height: ${({ _short }) => (_short ? 12 : 18)}px;
  text-wrap: auto;
`;

const ReactivateNowButton = styled.a`
  max-width: 142px;
  display: flex;
  padding: 7px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  background: #0ec12f;
  color: #fff;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  text-decoration: none;
`;

const CaptionContainer = styled.div<BlockType>`
  display: flex;
  align-items: center;
  margin-top: ${({ _short }) => (_short ? 4 : 0)}px;
`;

const Caption = styled.div<BlockType>`
  color: rgba(17, 17, 17, 0.5);
  font-size: ${({ _short }) => (_short ? 9 : 11)}px;
  font-style: normal;
  font-weight: 400;
  line-height: ${({ _short }) => (_short ? 10 : 12)}px;
  margin-left: 4px;
`;

const ShortContainer = styled.div`
  cursor: pointer;
  font-family: Arial, sans-serif;
  pointer-events: all !important;
  & * {
    pointer-events: all !important;
  }
  display: flex;
  max-width: 220px;
  min-height: 56px;
  padding: 8px;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  border-radius: 4px;
  border: 1px solid #ff3838;
  background: rgb(255, 236, 236);
  box-sizing: border-box;
`;

const ReactivateNowTitle = styled.a`
  color: #111;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: 12px;
  text-decoration: underline;
`;
